import { AlertTriangle, Link2, User } from "lucide-react";
import { useTranslation } from "react-i18next";

import { isCockpitDeliveryMissing } from "./cockpitDelivery";
import { RedeliverApplyButton } from "./RedeliverApplyButton";
import { InfoItem, infoItemIconClass } from "../../components/InfoItem";
import { InfoItemSkeleton } from "../../components/InfoItemSkeleton";
import type { AppliesIntegrationSectionProps } from "../integrationSections";

import { Alert, AlertDescription } from "@/components/ui/v2/alert";
import { useApplyCockpit } from "@/features/integrations/modules/applies/api";

export function CockpitApplyDetailSection({ apply }: AppliesIntegrationSectionProps) {
  const { t } = useTranslation();
  const { data: cockpit, isLoading } = useApplyCockpit(apply.id);

  // A missing match ID is the only trace an apply leaves when Cockpit never received it: the consumer
  // skips and completes the message when the client's applies module is inactive.
  const deliveryMissing =
    !isLoading &&
    isCockpitDeliveryMissing({
      matchId: cockpit?.matchId,
      dateSubmitted: apply.dateSubmitted,
    });

  return (
    <section>
      <h2 className="mb-3 text-lg font-semibold">
        {t("integrations.cockpit.applies.detail.cockpit")}
      </h2>
      {deliveryMissing && (
        <Alert variant="warning" className="mb-3 items-center [&>svg]:translate-y-0">
          <AlertTriangle className="size-4" />
          <AlertDescription className="w-full">
            <div className="flex w-full items-center justify-between gap-3">
              <span>{t("integrations.cockpit.applies.detail.redeliver.description")}</span>
              <RedeliverApplyButton applyId={apply.id} />
            </div>
          </AlertDescription>
        </Alert>
      )}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {isLoading ? (
          <>
            <InfoItemSkeleton />
            <InfoItemSkeleton />
          </>
        ) : (
          <>
            <InfoItem
              icon={<Link2 className={infoItemIconClass} />}
              label={t("integrations.cockpit.applies.detail.matchId")}
              value={cockpit?.matchId}
              href={cockpit?.matchUrl}
            />
            <InfoItem
              icon={<User className={infoItemIconClass} />}
              label={t("integrations.cockpit.applies.detail.candidateId")}
              value={cockpit?.candidateId}
              href={cockpit?.candidateUrl}
            />
          </>
        )}
      </div>
    </section>
  );
}
